import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ShieldAlert,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronUp, ChevronDown,
  Download, RefreshCw, AlertTriangle, Clock,
  Bot, ChevronDown as ExpandIcon,
  X, CheckCircle2, XCircle, ExternalLink, DollarSign,
  Tag, Layers, Activity, Info,
} from 'lucide-react'
import { useActions } from '../hooks/useActions.js'
import { useNow } from '../hooks/useNow.js'
import { trackEvent } from '../analytics/mixpanel.js'
import Tooltip from '../components/shared/Tooltip.jsx'
import LoadingSpinner, { SkeletonTable } from '../components/shared/LoadingSpinner.jsx'
import StatStrip from '../components/shared/StatStrip.jsx'
import CollapsibleFilterBar from '../components/shared/CollapsibleFilterBar.jsx'
import FilterField from '../components/shared/FilterField.jsx'
import ActionDetailDialog from '../components/shared/ActionDetailDialog.jsx'
import {
  CategoryIcon, PillarBadge, UrgencyBadge, LeadTimeBadge,
} from '../components/shared/ActionBadges.jsx'
import {
  CATEGORY_META, fmtPnl,
} from '../utils/actionVisuals.js'
import { ACTION_TIPS } from '../utils/actionTips.js'
import { DC_CODES, dcLabel } from '../utils/dc.js'
import client from '../api/client.js'
import { leadTimeHours, leadTimeTier } from '../utils/leadTime.js'

const PAGE_SIZE = 50

// CATEGORY_META, PILLAR_COLORS, URGENCY_COLORS, fmtPnl, and the badge
// primitives moved to shared modules (utils/actionVisuals.js +
// components/shared/ActionBadges.jsx) so DoneActions can render an
// identically-styled card. Imports above.

const PILLARS = ['all', 'Asset Liquidity', 'Labor Preservation', 'Process Velocity']
const URGENCIES = ['all', 'Immediate (24h)', 'Proactive (48–96h)']
const DCS = ['all', ...DC_CODES]
const CATEGORIES = [
  'all', 'LABOR_FLEX', 'LABOR_SURGE_PREP', 'PREDEPLOYMENT',
  'ALLOCATION_HOLD', 'REALLOCATION_OPPORTUNITY', 'DC_YARD_CLEARANCE',
  'SURGE_WARNING', 'INBOUND_RISK', 'LANE_RISK',
]
const CATEGORY_LABELS = {
  all: 'All Types',
  LABOR_FLEX: 'Labor Flex',
  LABOR_SURGE_PREP: 'Surge Prep',
  PREDEPLOYMENT: 'Pre-deploy',
  ALLOCATION_HOLD: 'Hold',
  REALLOCATION_OPPORTUNITY: 'Reallocate',
  DC_YARD_CLEARANCE: 'Yard Clear',
  SURGE_WARNING: 'Surge Warning',
  INBOUND_RISK: 'Inbound Risk',
  LANE_RISK: 'Lane Risk',
}

// ACTION_TIPS moved to utils/actionTips.js — used by ActionDetailDialog only.

// ── Mobile-friendly card variant of a table row.
// Same data, vertically stacked — no checkbox/sort affordances (those don't
// apply on touch). Used below md; the desktop table is unchanged.
function ActionCard({
  action,
  isAck,
  expandedBriefing,
  briefings,
  now,
  setDetailAction,
  toggleBriefing,
  navigate,
}) {
  const meta = CATEGORY_META[action.category] ?? {}
  const targetClickable = action.store_id || action.category === 'DC_YARD_CLEARANCE'
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      onClick={() => setDetailAction(action)}
      className={`glass-card p-3 cursor-pointer transition-colors ${
        isAck ? 'opacity-40' : 'active:bg-white/[0.04]'
      }`}
    >
      <div className="flex items-start gap-3">
        <CategoryIcon category={action.category} />
        <div className="flex-1 min-w-0">
          {/* Title + P&L */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className={`text-xs font-black uppercase tracking-wide ${meta.color ?? 'text-slate-300'}`}>
                {action.action_text}
              </div>
              <div
                className={`text-xs text-slate-300 mt-0.5 truncate ${targetClickable ? 'active:text-cyan-300' : ''}`}
                onClick={(e) => {
                  if (!targetClickable) return
                  e.stopPropagation()
                  if (action.store_id) navigate(`/stores/${action.store_id}`)
                  else navigate(`/allocation?dc=${action.dc}`)
                }}
              >
                {action.target}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-bold text-emerald-400">{fmtPnl(action.pnl_estimate ?? 0)}</div>
              {action.pnl_label && <div className="text-[10px] text-slate-600">{action.pnl_label}</div>}
            </div>
          </div>

          {/* Badge row */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <PillarBadge pillar={action.pillar} />
            <UrgencyBadge
              urgency={action.urgency}
              reason={action.urgency_reason}
              wos={action.wos}
              leadTimeHours={leadTimeHours(action.event_at, now)}
            />
            <LeadTimeBadge eventAt={action.event_at} now={now} />
            {action.promo_multiplier > 1 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/20">
                {action.promo_multiplier === 2.5 ? '🔥 BF/Cyber' : '⚡ Promo'} {action.promo_multiplier}×
              </span>
            )}
          </div>

          {/* Justification */}
          <p className="text-xs text-slate-400 leading-snug mt-2 line-clamp-2">
            {action.justification}
          </p>

          {/* Sub-financials */}
          {(action.margin_risk > 0 || action.labor_cost > 0) && (
            <div className="flex gap-3 mt-1">
              {action.margin_risk > 0 && (
                <span className="text-[10px] text-amber-400/80">Margin: {fmtPnl(action.margin_risk)}</span>
              )}
              {action.labor_cost > 0 && (
                <span className="text-[10px] text-cyan-400/80">Labor: ${action.labor_cost.toFixed(0)}</span>
              )}
            </div>
          )}

          {isAck && (
            <div className="text-[10px] text-emerald-500 mt-1">✓ Acknowledged</div>
          )}

          {/* Briefing toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleBriefing(action.id) }}
            className="mt-2 flex items-center gap-1 text-[10px] text-slate-500 active:text-cyan-400"
          >
            <Bot size={9} />
            Manager Briefing
            <ExpandIcon size={9} className={`transition-transform ${expandedBriefing === action.id ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {expandedBriefing === action.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-2.5 flex items-start gap-2">
                  <Bot size={11} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-1">
                      Manager Briefing — AI Generated
                    </div>
                    {briefings[action.id]?.loading ? (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <RefreshCw size={10} className="animate-spin" />
                        Generating…
                      </div>
                    ) : briefings[action.id]?.error ? (
                      <p className="text-xs text-slate-500">Briefing unavailable.</p>
                    ) : (
                      <p className="text-xs text-slate-300 leading-relaxed">
                        {briefings[action.id]?.text}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

// ── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ col, sortBy, sortDir }) {
  if (sortBy !== col) return <ChevronUp size={11} className="opacity-20 ml-0.5" />
  return sortDir === 'asc'
    ? <ChevronUp size={11} className="text-cyan-400 ml-0.5" />
    : <ChevronDown size={11} className="text-cyan-400 ml-0.5" />
}

// ── Export helpers ────────────────────────────────────────────────────────────

function exportToCSV(actions, nowMs = Date.now()) {
  const headers = ['ID', 'Category', 'Pillar', 'Urgency', 'DC', 'Target', 'Action', 'Justification', 'Trigger Date', 'Event At', 'Lead Time (h)', 'P&L Label', 'P&L Estimate ($)']
  const rows = actions.map((a) => {
    const lead = leadTimeHours(a.event_at, nowMs)
    return [
      a.id, a.category, a.pillar, a.urgency, a.dc,
      `"${(a.target ?? '').replace(/"/g, '""')}"`,
      a.action_text,
      `"${(a.justification ?? '').replace(/"/g, '""')}"`,
      a.trigger_date,
      a.event_at ?? '',
      lead == null ? '' : lead.toFixed(1),
      a.pnl_label,
      a.pnl_estimate ?? 0,
    ]
  })
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `aeo_action_items_${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Briefing hover tooltip ────────────────────────────────────────────────────

function BriefingHoverTooltip({ hoverTooltip, briefings }) {
  if (!hoverTooltip) return null
  const { id, rect } = hoverTooltip
  const briefing = briefings[id]

  const spaceBelow = window.innerHeight - rect.bottom
  const above = spaceBelow < 200

  const tooltipStyle = {
    position: 'fixed',
    zIndex: 45,
    width: 420,
    left: Math.min(rect.left + 48, window.innerWidth - 436),
    pointerEvents: 'none',
    background: 'rgba(6, 12, 36, 0.97)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 0 24px rgba(0,212,255,0.1), 0 12px 48px rgba(0,0,0,0.75)',
    border: '1px solid rgba(0,212,255,0.22)',
    borderRadius: '0.75rem',
    padding: '1rem',
    ...(above
      ? { bottom: window.innerHeight - rect.top + 6 }
      : { top: rect.bottom + 6 }),
  }

  return createPortal(
    <motion.div
      style={tooltipStyle}
      initial={{ opacity: 0, y: above ? 6 : -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <Bot size={12} className="text-cyan-400 flex-shrink-0" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">
          AI Manager Briefing
        </span>
      </div>
      {!briefing || briefing.loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <RefreshCw size={10} className="animate-spin text-cyan-400" />
          Generating briefing…
        </div>
      ) : briefing.error ? (
        <p className="text-xs text-slate-500 italic">Briefing unavailable.</p>
      ) : (
        <p className="text-xs text-slate-300 leading-relaxed line-clamp-5">{briefing.text}</p>
      )}
    </motion.div>,
    document.body,
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ActionCenter() {
  const navigate = useNavigate()

  const [pillar, setPillar] = useState('all')
  const [urgency, setUrgency] = useState('all')
  const [dc, setDc] = useState('all')
  const [category, setCategory] = useState('all')

  // RF-T04 scope tab; RF-T03 low-risk suppression. Both persisted to localStorage.
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem('aeo:actions:scope') || 'all' } catch { return 'all' }
  })
  const [hideLowRisk, setHideLowRisk] = useState(() => {
    try { return localStorage.getItem('aeo:actions:hideLowRisk') === '1' } catch { return false }
  })
  const [lowRiskThreshold, setLowRiskThreshold] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('aeo:actions:lowRiskThreshold') || '1000', 10)
      return Number.isFinite(v) && v >= 0 ? v : 1000
    } catch { return 1000 }
  })
  const [hidePast, setHidePast] = useState(() => {
    try { return localStorage.getItem('aeo:actions:hidePast') === '1' } catch { return false }
  })
  useEffect(() => { try { localStorage.setItem('aeo:actions:scope', scope) } catch {} }, [scope])
  useEffect(() => { try { localStorage.setItem('aeo:actions:hideLowRisk', hideLowRisk ? '1' : '0') } catch {} }, [hideLowRisk])
  useEffect(() => { try { localStorage.setItem('aeo:actions:lowRiskThreshold', String(lowRiskThreshold)) } catch {} }, [lowRiskThreshold])
  useEffect(() => { try { localStorage.setItem('aeo:actions:hidePast', hidePast ? '1' : '0') } catch {} }, [hidePast])

  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('urgency')
  const [sortDir, setSortDir] = useState('asc')

  // Lead-Time clock — re-renders this page every 10 minutes so the
  // hours-until-event values stay in sync with the wall clock without
  // re-fetching the action list (action cache is server-side, 30 min TTL).
  const now = useNow(10 * 60 * 1000)

  const [acknowledged, setAcknowledged] = useState(new Set())
  const [selected, setSelected] = useState(new Set())
  const [refreshing, setRefreshing] = useState(false)
  const [expandedBriefing, setExpandedBriefing] = useState(null)  // action_id or null
  const [briefings, setBriefings] = useState({})  // action_id → {text, loading, error}
  const [detailAction, setDetailAction] = useState(null)  // action shown in detail dialog
  const [hoverTooltip, setHoverTooltip] = useState(null) // { id, rect }
  const hoverTimerRef = useRef(null)

  const fetchBriefing = useCallback(async (actionId) => {
    if (briefings[actionId]) return  // already loaded
    setBriefings((prev) => ({ ...prev, [actionId]: { loading: true } }))
    try {
      const { data } = await client.get(`/actions/${actionId}/briefing`)
      setBriefings((prev) => ({ ...prev, [actionId]: { text: data.briefing, loading: false } }))
    } catch {
      setBriefings((prev) => ({ ...prev, [actionId]: { error: true, loading: false } }))
    }
  }, [briefings])

  const toggleBriefing = (actionId) => {
    if (expandedBriefing === actionId) {
      setExpandedBriefing(null)
    } else {
      setExpandedBriefing(actionId)
      fetchBriefing(actionId)
    }
  }

  const handleFilter = (setter, filterName) => (val) => {
    if (filterName) {
      trackEvent('Filter Changed', { page: 'actions', filter_name: filterName, filter_value: val })
    }
    setter(val)
    setPage(1)
  }

  const handleSort = (col) => {
    const nextDir = sortBy === col
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : (col === 'pnl_estimate' ? 'desc' : 'asc')
    trackEvent('Sort Changed', { page: 'actions', column: col, direction: nextDir })
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortDir(col === 'pnl_estimate' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const { data, isLoading, isError, refetch, isFetching } = useActions({
    pillar, urgency, dc, category, scope, hideLowRisk, lowRiskThreshold,
    sortBy, sortDir, page, pageSize: PAGE_SIZE,
  })

  const rawActions = data?.actions ?? []
  const totalPages = data?.total_pages ?? 1

  // Client-side past filter — pastness depends on the real-time clock, not server data.
  const pastCount = useMemo(
    () => rawActions.filter((a) => leadTimeTier(leadTimeHours(a.event_at, now)) === 'past').length,
    [rawActions, now],
  )
  const actions = useMemo(
    () => hidePast
      ? rawActions.filter((a) => leadTimeTier(leadTimeHours(a.event_at, now)) !== 'past')
      : rawActions,
    [rawActions, hidePast, now],
  )

  // ── Checkbox logic ──────────────────────────────────────────────────────────
  const allIds = useMemo(() => new Set(actions.map((a) => a.id)), [actions])
  const allSelected = allIds.size > 0 && [...allIds].every((id) => selected.has(id))

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) allIds.forEach((id) => next.delete(id))
      else allIds.forEach((id) => next.add(id))
      return next
    })
  }

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const acknowledgeSelected = () => {
    setAcknowledged((prev) => {
      const next = new Set(prev)
      selected.forEach((id) => next.add(id))
      return next
    })
    setSelected(new Set())
  }

  // Build the rich property bag for accept/dismiss events from the in-memory action list.
  const actionEventProps = (actionId) => {
    const a = rawActions.find((x) => x.id === actionId)
    if (!a) return { action_id: actionId }
    return {
      action_id: actionId,
      action_category: a.category,
      node_type: a.node_type,
      pillar: a.pillar,
      severity: a.trigger_severity,
      urgency: a.urgency,
      financial_impact_dollars: a.pnl_estimate ?? 0,
    }
  }

  // Two terminal outcomes, distinct event_types so the Done page can render
  // the Accepted vs Dismissed split and retrospective analysis can slice on
  // outcome ("did we dismiss too aggressively?").
  const handleAccept = async (actionId) => {
    trackEvent('Action Accepted', actionEventProps(actionId))
    try {
      await client.post(`/actions/${actionId}/done`)
    } catch (_) {}
    setAcknowledged((prev) => { const n = new Set(prev); n.add(actionId); return n })
  }

  const handleDismiss = async (actionId) => {
    trackEvent('Action Dismissed', actionEventProps(actionId))
    try {
      await client.post(`/actions/${actionId}/dismiss`)
    } catch (_) {}
    setAcknowledged((prev) => { const n = new Set(prev); n.add(actionId); return n })
  }

  // Fire once per dialog open. Re-renders with the same id are deduped by the effect key.
  useEffect(() => {
    if (!detailAction?.id) return
    trackEvent('Action Details Opened', actionEventProps(detailAction.id))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailAction?.id])

  const handleRefresh = async () => {
    trackEvent('Actions Refreshed', { source: 'manual' })
    setRefreshing(true)
    try {
      await client.post('/actions/refresh')
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  const [exporting, setExporting] = useState(false)

  const exportSelected = async () => {
    let toExport
    if (selected.size > 0) {
      toExport = actions.filter((a) => selected.has(a.id))
    } else {
      // "Export All" must cover every page for the active filters, not just
      // the page currently in memory. Walk the paginated endpoint to the end.
      setExporting(true)
      try {
        toExport = []
        let p = 1
        let pages = 1
        do {
          const { data: pageData } = await client.get('/actions', {
            params: {
              pillar, urgency, dc, category, scope,
              hide_low_risk: hideLowRisk, low_risk_threshold: lowRiskThreshold,
              sort_by: sortBy, sort_dir: sortDir, page: p, page_size: 200,
            },
          })
          toExport.push(...(pageData.actions ?? []))
          pages = pageData.total_pages ?? 1
          p += 1
        } while (p <= pages)
      } finally {
        setExporting(false)
      }
    }
    trackEvent('Actions Exported', {
      format: 'csv',
      row_count: toExport.length,
      selection_mode: selected.size > 0 ? 'selected' : 'all',
    })
    exportToCSV(toExport, now)
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-4 flex flex-col min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)]">
      {/* ── Header ── */}
      <div className="mb-6">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-3"
        >
          <div>
            <h1 className="text-2xl font-black text-slate-100 flex items-center gap-2">
              <ShieldAlert size={20} className="text-cyan-400" />
              Action Center
              <Tooltip content="Prescriptive logistics actions generated from live weather forecasts × store intelligence" placement="bottom" asButton>
                <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
              </Tooltip>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing || isFetching}
              className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            {/* Hidden below md: the header Export-All button. Users on
                true-mobile widths still get an Export action via the
                bulk-actions strip when rows are selected. */}
            <button
              onClick={exportSelected}
              disabled={exporting}
              className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 text-xs font-semibold transition-colors disabled:opacity-50"
            >
              <Download size={12} />
              {selected.size > 0
                ? `Export (${selected.size})`
                : exporting ? 'Exporting…' : 'Export All'}
            </button>
          </div>
        </motion.div>
      </div>

      {/* ── Summary stats ── */}
      {data && (
        <StatStrip
          items={[
            { label: 'Total Actions', value: data.total.toLocaleString(), icon: Activity, tone: 'slate' },
            { label: 'Immediate (24h)', value: data.immediate_count.toLocaleString(), icon: AlertTriangle, tone: 'danger' },
            { label: 'Proactive', value: data.proactive_count.toLocaleString(), icon: Clock, tone: 'warning' },
            { label: 'P&L Potential', value: fmtPnl(data.total_pnl_estimate ?? 0), icon: DollarSign, tone: 'success' },
          ]}
        />
      )}

      {/* ── Filter bar ── */}
      <CollapsibleFilterBar
        persistKey="aeo:filters:open:actions"
        activeSummary={[
          scope !== 'all' && `Scope: ${scope === 'dc' ? 'DC' : 'Store'}`,
          pillar !== 'all' && `Pillar: ${pillar.split(' ')[0]}`,
          urgency !== 'all' && (urgency.startsWith('Immediate') ? 'Immediate' : 'Proactive'),
          dc !== 'all' && `DC: ${dcLabel(dc)}`,
          category !== 'all' && (CATEGORY_LABELS[category] ?? category),
          hideLowRisk && `Hide < $${(lowRiskThreshold / 1000).toFixed(lowRiskThreshold % 1000 === 0 ? 0 : 1)}K${(data?.hidden_low_risk_count ?? 0) > 0 ? ` (${data.hidden_low_risk_count} hidden)` : ''}`,
          hidePast && pastCount > 0 && `Hide past (${pastCount})`,
        ].filter(Boolean).join(' · ') || 'All actions'}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
          {/* Scope filter (RF-T04) — node type: all / DC / store */}
          <FilterField label="Scope">
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {[
                { value: 'all',   label: 'All',   count: data ? (data.by_scope?.store ?? 0) + (data.by_scope?.dc ?? 0) : null },
                { value: 'dc',    label: 'DC',    count: data?.by_scope?.dc },
                { value: 'store', label: 'Store', count: data?.by_scope?.store },
              ].map(({ value, label, count }) => (
                <button
                  key={value}
                  onClick={() => { handleFilter(setScope, 'scope')(value) }}
                  className={`px-3 py-2 text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                    scope === value
                      ? 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {label}
                  {count != null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      scope === value ? 'bg-cyan-500/30' : 'bg-white/5'
                    }`}>{count}</span>
                  )}
                </button>
              ))}
            </div>
          </FilterField>

          {/* Pillar filter */}
          <FilterField label="Pillar">
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {PILLARS.map((p) => (
                <button
                  key={p}
                  onClick={() => handleFilter(setPillar, 'pillar')(p)}
                  className={`px-3 py-2 text-xs font-semibold transition-colors ${
                    pillar === p
                      ? p === 'Asset Liquidity'
                        ? 'bg-amber-500/20 text-amber-300'
                        : p === 'Labor Preservation'
                        ? 'bg-cyan-500/20 text-cyan-300'
                        : p === 'Process Velocity'
                        ? 'bg-purple-500/20 text-purple-300'
                        : 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {p === 'all' ? 'All Pillars' : p.split(' ')[0]}
                </button>
              ))}
            </div>
          </FilterField>

          {/* Urgency filter */}
          <FilterField label="Urgency">
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {URGENCIES.map((u) => (
                <button
                  key={u}
                  onClick={() => handleFilter(setUrgency, 'urgency')(u)}
                  className={`px-3 py-2 text-xs font-semibold transition-colors ${
                    urgency === u
                      ? u.startsWith('Immediate')
                        ? 'bg-red-500/20 text-red-300'
                        : u.startsWith('Proactive')
                        ? 'bg-amber-500/15 text-amber-300'
                        : 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {u === 'all' ? 'All Urgency' : u.startsWith('Immediate') ? 'Immediate' : 'Proactive'}
                </button>
              ))}
            </div>
          </FilterField>

          {/* DC filter */}
          <FilterField label="Distribution center">
            <select
              value={dc}
              onChange={(e) => handleFilter(setDc, 'dc')(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {DCS.map((d) => (
                <option key={d} value={d}>{dcLabel(d)}</option>
              ))}
            </select>
          </FilterField>

          {/* Category filter */}
          <FilterField label="Category">
            <select
              value={category}
              onChange={(e) => handleFilter(setCategory, 'category')(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>
              ))}
            </select>
          </FilterField>

          </div>

          {/* Display options — change what's shown, separated from the data filters above */}
          <div className="border-t border-white/5 pt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Display</span>
            <div className="flex flex-wrap items-center gap-2 min-h-[34px]">
              <Tooltip
                content={`Hide actions with P&L impact below the threshold. ${
                  data?.hidden_low_risk_count ?? 0
                } currently ${hideLowRisk ? 'hidden' : 'below threshold'}.`}
                placement="bottom"
              >
                <label className="flex items-center gap-2 cursor-pointer select-none px-2 py-1 rounded hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={hideLowRisk}
                    onChange={(e) => { handleFilter(setHideLowRisk, 'hide_low_risk')(e.target.checked) }}
                    className="w-3.5 h-3.5 rounded accent-cyan-500"
                  />
                  <span className="text-xs font-semibold text-slate-300">Hide low-risk</span>
                  <span className="text-[10px] text-slate-500">
                    &lt; ${(lowRiskThreshold / 1000).toFixed(lowRiskThreshold % 1000 === 0 ? 0 : 1)}K
                  </span>
                </label>
              </Tooltip>
              <input
                type="number"
                min={0}
                step={500}
                value={lowRiskThreshold}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  handleFilter(setLowRiskThreshold, 'low_risk_threshold')(Number.isFinite(v) && v >= 0 ? v : 0)
                }}
                className="w-20 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50"
                title="Low-risk threshold ($)"
              />
              {(hideLowRisk && (data?.hidden_low_risk_count ?? 0) > 0) && (
                <span className="text-[10px] text-slate-400">
                  {data.hidden_low_risk_count} hidden
                </span>
              )}

              <Tooltip content="Hide actions whose event has already passed. These are still visible in the export." placement="bottom">
                <label className="flex items-center gap-2 cursor-pointer select-none px-2 py-1 rounded hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={hidePast}
                    onChange={(e) => setHidePast(e.target.checked)}
                    className="w-3.5 h-3.5 rounded accent-cyan-500"
                  />
                  <span className="text-xs font-semibold text-slate-300">Hide past</span>
                  {pastCount > 0 && (
                    <span className="text-[10px] text-slate-500">{pastCount} past</span>
                  )}
                </label>
              </Tooltip>

              {isFetching && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-3.5 h-3.5 border border-cyan-400 border-t-transparent rounded-full"
                />
              )}
            </div>
          </div>
        </div>
      </CollapsibleFilterBar>

      {/* Bulk actions — sibling of filter bar so they stay visible when filters collapse */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card mb-4 overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="text-xs text-slate-400">{selected.size} selected</span>
              <button
                onClick={acknowledgeSelected}
                className="px-3 py-1 rounded text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
              >
                Acknowledge
              </button>
              <button
                onClick={exportSelected}
                className="hidden md:block px-3 py-1 rounded text-xs font-semibold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
              >
                Export Selected
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="px-3 py-1 rounded text-xs text-slate-500 hover:text-slate-300 transition-colors ml-auto"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Action Grid ── */}
      {isLoading ? (
        <div className="glass-card p-6">
          <SkeletonTable rows={10} />
        </div>
      ) : isError ? (
        <div className="glass-card p-8 text-center">
          <AlertTriangle size={40} className="text-amber-400 mx-auto mb-3" />
          <p className="text-slate-400 mb-4">Failed to load action items</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors text-sm"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="glass-card overflow-hidden flex flex-col xl:flex-1 xl:min-h-0">
          {/* Desktop (xl+, 1280+): table fills remaining viewport space
              and scrolls internally — the original behavior.
              Below xl (150% zoom desktop OR smaller monitor OR tablet):
              table has a bounded height (500–700px) with internal scroll.
              The page grows beyond the viewport and the browser handles
              page scroll. This keeps the table feeling like the 100%-zoom
              reference instead of getting squeezed by the fixed-height
              parent. */}
          <div className="hidden md:block overflow-auto min-h-[300px] max-h-[min(70vh,700px)] xl:flex-1 xl:min-h-0 xl:max-h-none">
            <table className="w-full text-sm">
              {/* Sticky header — top-16 clears the fixed Navbar (pt-16 in App.jsx).
                  Opaque background so scrolled rows don't bleed through.
                  z-20 stays above row hover effects but below the action detail dialog (z-50). */}
              <thead className="sticky top-0 z-20 bg-bg-base/95 backdrop-blur-sm">
                <tr className="border-b border-white/10">
                  <th className="p-3 text-left w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="w-3.5 h-3.5 rounded accent-cyan-500 cursor-pointer"
                    />
                  </th>
                  {/* Action / Target — sort by category */}
                  <th className="p-3 text-left">
                    <Tooltip content="Action type and target store or DC cluster — click to sort by category" placement="bottom">
                      <button
                        onClick={() => handleSort('category')}
                        className="inline-flex items-center text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        Action / Target
                        <SortIcon col="category" sortBy={sortBy} sortDir={sortDir} />
                      </button>
                    </Tooltip>
                  </th>
                  {/* Pillar */}
                  <th className="p-3 text-center">
                    <Tooltip content="Strategic pillar: Asset Liquidity · Labor Preservation · Process Velocity" placement="bottom">
                      <button
                        onClick={() => handleSort('pillar')}
                        className="inline-flex items-center mx-auto text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        Pillar
                        <SortIcon col="pillar" sortBy={sortBy} sortDir={sortDir} />
                      </button>
                    </Tooltip>
                  </th>
                  {/* Urgency */}
                  <th className="p-3 text-center">
                    <Tooltip content="Immediate = within 24h (severe weather or critical WOS). Proactive = 48–96h planning window." placement="bottom">
                      <button
                        onClick={() => handleSort('urgency')}
                        className="inline-flex items-center mx-auto text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        Urgency
                        <SortIcon col="urgency" sortBy={sortBy} sortDir={sortDir} />
                      </button>
                    </Tooltip>
                  </th>
                  {/* Lead Time — hours until the actual event (refreshed every 10 min) */}
                  <th className="p-3 text-center">
                    <Tooltip content="Hours until the underlying event (storm, promo, etc). Refreshed every 10 minutes." placement="bottom">
                      <button
                        onClick={() => handleSort('event_at')}
                        className="inline-flex items-center mx-auto text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        Lead Time
                        <SortIcon col="event_at" sortBy={sortBy} sortDir={sortDir} />
                      </button>
                    </Tooltip>
                  </th>
                  {/* Justification — not sortable, just a label */}
                  <th className="p-3 text-left">
                    <Tooltip content="Data-backed reason this action was triggered" placement="bottom">
                      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 cursor-help">
                        Justification
                      </span>
                    </Tooltip>
                  </th>
                  {/* P&L */}
                  <th className="p-3 text-right">
                    <Tooltip content="Estimated P&L impact: labor savings, freight avoidance, or protected inventory value" placement="bottom">
                      <button
                        onClick={() => handleSort('pnl_estimate')}
                        className="inline-flex items-center ml-auto text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        P&L Impact
                        <SortIcon col="pnl_estimate" sortBy={sortBy} sortDir={sortDir} />
                      </button>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                  {actions.map((action, i) => {
                    const meta = CATEGORY_META[action.category] ?? {}
                    const isAck = acknowledged.has(action.id)
                    const isSel = selected.has(action.id)
                    return (
                      <React.Fragment key={action.id}>
                      <motion.tr
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: Math.min(i * 0.012, 0.25) }}
                        className={`border-b border-white/[0.03] transition-colors cursor-pointer ${
                          isAck ? 'opacity-40' : isSel ? 'bg-cyan-500/5' : 'hover:bg-white/[0.04]'
                        }`}
                        onClick={() => setDetailAction(action)}
                        onMouseEnter={(e) => {
                          fetchBriefing(action.id)
                          const rect = e.currentTarget.getBoundingClientRect()
                          clearTimeout(hoverTimerRef.current)
                          hoverTimerRef.current = setTimeout(() => setHoverTooltip({ id: action.id, rect }), 350)
                        }}
                        onMouseLeave={() => {
                          clearTimeout(hoverTimerRef.current)
                          setHoverTooltip(null)
                        }}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(action.id)}
                            className="w-3.5 h-3.5 rounded accent-cyan-500 cursor-pointer"
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-start gap-2.5">
                            <CategoryIcon category={action.category} />
                            <div className="min-w-0">
                              <Tooltip content={ACTION_TIPS[action.action_text]} placement="top">
                                <div className={`text-xs font-black uppercase tracking-wide cursor-help border-b border-dashed border-current/40 inline-block ${meta.color ?? 'text-slate-300'}`}>
                                  {action.action_text}
                                </div>
                              </Tooltip>
                              <div
                                className={`text-slate-300 text-xs mt-0.5 transition-colors truncate max-w-[200px] ${
                                  action.store_id || action.category === 'DC_YARD_CLEARANCE'
                                    ? 'hover:text-cyan-300 cursor-pointer'
                                    : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (action.store_id) navigate(`/stores/${action.store_id}`)
                                  else if (action.category === 'DC_YARD_CLEARANCE') navigate(`/allocation?dc=${action.dc}`)
                                }}
                                title={action.target}
                              >
                                {action.target}
                              </div>
                              {isAck && (
                                <div className="text-[10px] text-emerald-500 mt-0.5">
                                  ✓ Acknowledged
                                </div>
                              )}
                              {/* Manager Briefing toggle */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleBriefing(action.id) }}
                                className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-500 hover:text-cyan-400 transition-colors"
                              >
                                <Bot size={9} />
                                Manager Briefing
                                <ExpandIcon
                                  size={9}
                                  className={`transition-transform ${expandedBriefing === action.id ? 'rotate-180' : ''}`}
                                />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <PillarBadge pillar={action.pillar} />
                        </td>
                        <td className="p-3 text-center">
                          <UrgencyBadge
                            urgency={action.urgency}
                            reason={action.urgency_reason}
                            wos={action.wos}
                            leadTimeHours={leadTimeHours(action.event_at, now)}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <LeadTimeBadge eventAt={action.event_at} now={now} />
                        </td>
                        <td className="p-3 max-w-[320px]">
                          <p className="text-xs text-slate-400 leading-snug line-clamp-2" title={action.justification}>
                            {action.justification}
                          </p>
                        </td>
                        <td className="p-3 text-right">
                          <div className="text-right">
                            <div className="text-sm font-bold text-emerald-400">
                              {fmtPnl(action.pnl_estimate ?? 0)}
                            </div>
                            <div className="text-[10px] text-slate-600">{action.pnl_label}</div>
                            {/* Financial Impact sub-section */}
                            {(action.margin_risk > 0 || action.labor_cost > 0) && (
                              <div className="mt-1.5 text-right space-y-0.5">
                                {action.margin_risk > 0 && (
                                  <div className="text-[10px] text-amber-400/80">
                                    Margin risk: {fmtPnl(action.margin_risk)}
                                  </div>
                                )}
                                {action.labor_cost > 0 && (
                                  <div className="text-[10px] text-cyan-400/80">
                                    Labor: ${action.labor_cost.toFixed(0)}
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Promo context badge */}
                            {action.promo_multiplier > 1 && (
                              <div className="mt-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/20">
                                {action.promo_multiplier === 2.5 ? '🔥 BF/Cyber' : '⚡ Promo'} {action.promo_multiplier}×
                              </div>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                      {/* Briefing expansion row */}
                      <AnimatePresence>
                        {expandedBriefing === action.id && (
                          <motion.tr
                            key={`briefing-${action.id}`}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <td colSpan={8} className="px-4 pb-3 pt-0">
                              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3 flex items-start gap-2">
                                <Bot size={13} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-1">
                                    Manager Briefing — AI Generated
                                  </div>
                                  {briefings[action.id]?.loading ? (
                                    <div className="flex items-center gap-2 text-xs text-slate-400">
                                      <RefreshCw size={10} className="animate-spin" />
                                      Generating briefing…
                                    </div>
                                  ) : briefings[action.id]?.error ? (
                                    <p className="text-xs text-slate-500">Briefing unavailable — Ollama offline.</p>
                                  ) : (
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                      {briefings[action.id]?.text}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </AnimatePresence>
                      </React.Fragment>
                    )
                  })}
              </tbody>
            </table>

            {actions.length === 0 && !isLoading && (
              <div className="p-12 text-center text-slate-500">
                No action items match the selected filters
              </div>
            )}
          </div>

          {/* True-mobile only: card list (selection/sort UI doesn't apply
              on touch). md+ uses the table above. No flex-1/overflow:
              below md the page scrolls naturally. */}
          <div className="md:hidden p-3 flex flex-col gap-3">
            {actions.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                isAck={acknowledged.has(action.id)}
                expandedBriefing={expandedBriefing}
                briefings={briefings}
                now={now}
                setDetailAction={setDetailAction}
                toggleBriefing={toggleBriefing}
                navigate={navigate}
              />
            ))}
            {actions.length === 0 && !isLoading && (
              <div className="p-8 text-center text-slate-500">
                No action items match the selected filters
              </div>
            )}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
              <span className="text-xs text-slate-500">
                Page {data?.page} of {totalPages} • {data?.total.toLocaleString()} actions
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    trackEvent('Pagination Changed', { page: 'actions', to_page: 1, total_pages: totalPages })
                    setPage(1)
                  }}
                  disabled={page === 1}
                  className="hidden md:block p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  onClick={() => {
                    const next = Math.max(1, page - 1)
                    trackEvent('Pagination Changed', { page: 'actions', to_page: next, total_pages: totalPages })
                    setPage(next)
                  }}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i
                  return (
                    <button
                      key={p}
                      onClick={() => {
                        trackEvent('Pagination Changed', { page: 'actions', to_page: p, total_pages: totalPages })
                        setPage(p)
                      }}
                      className={`hidden md:block px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        p === page
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {p}
                    </button>
                  )
                })}
                <button
                  onClick={() => {
                    const next = Math.min(totalPages, page + 1)
                    trackEvent('Pagination Changed', { page: 'actions', to_page: next, total_pages: totalPages })
                    setPage(next)
                  }}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => {
                    trackEvent('Pagination Changed', { page: 'actions', to_page: totalPages, total_pages: totalPages })
                    setPage(totalPages)
                  }}
                  disabled={page === totalPages}
                  className="hidden md:block p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Action Detail Dialog ── */}
      <AnimatePresence>
        {detailAction && (
          <ActionDetailDialog
            key={detailAction.id}
            action={detailAction}
            onClose={() => setDetailAction(null)}
            onAccept={handleAccept}
            onDismiss={handleDismiss}
          />
        )}
      </AnimatePresence>

      {/* ── Briefing hover tooltip ── */}
      <AnimatePresence>
        {hoverTooltip && !detailAction && (
          <BriefingHoverTooltip
            key={hoverTooltip.id}
            hoverTooltip={hoverTooltip}
            briefings={briefings}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
